<template>
  <el-dialog
    :visible.sync="dialogVisible"
    append-to-body
    class="space-management-dialog"
    custom-class="space-management-dialog__panel"
    title="空间管理"
    top="5vh"
    width="880px"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <el-tab-pane label="我的空间" name="spaces">
        <div class="space-toolbar">
          <div class="space-toolbar__identity">
            <span>当前空间</span>
            <strong>{{ currentSpaceName }}</strong>
          </div>
          <el-button
            icon="el-icon-plus"
            size="small"
            type="primary"
            @click="showCreateForm = !showCreateForm"
          >创建空间</el-button>
        </div>

        <form v-if="showCreateForm" class="space-create-form" @submit.prevent="handleCreateSpace">
          <el-input
            v-model.trim="createForm.name"
            maxlength="100"
            placeholder="空间名称"
            show-word-limit
          />
          <el-input
            v-model.trim="createForm.slug"
            maxlength="64"
            placeholder="英文标识（可选）"
          />
          <div class="space-create-form__actions">
            <el-button size="small" @click="resetCreateForm">取消</el-button>
            <el-button :loading="creating" native-type="submit" size="small" type="primary">确认创建</el-button>
          </div>
        </form>

        <div v-loading="loadingSpaces" class="space-list" aria-live="polite">
          <el-empty v-if="!loadingSpaces && !spaces.length" description="暂无可用空间" :image-size="72" />
          <div
            v-for="space in spaces"
            :key="space.id"
            class="space-row"
            :class="{ 'is-current': isCurrentSpace(space) }"
          >
            <span class="space-avatar" aria-hidden="true">{{ spaceInitial(space) }}</span>
            <div class="space-row__main">
              <div class="space-row__title">
                <strong>{{ spaceLabel(space) }}</strong>
                <el-tag v-if="isCurrentSpace(space)" size="mini" type="warning">当前</el-tag>
                <el-tag v-if="space.is_default" size="mini" type="info">默认</el-tag>
              </div>
            </div>
            <div class="space-row__actions">
              <el-button
                v-if="!space.is_default"
                :loading="defaultingSpaceID === space.id"
                size="mini"
                type="text"
                @click="handleSetDefault(space)"
              >设为默认</el-button>
              <el-button
                :disabled="isCurrentSpace(space)"
                :loading="switchingSpaceID === space.id"
                size="mini"
                @click="handleSwitchSpace(space)"
              >{{ isCurrentSpace(space) ? "已进入" : "切换" }}</el-button>
              <el-button
                class="space-row__leave"
                size="mini"
                type="text"
                @click="handleLeaveSpace(space)"
              >退出</el-button>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="成员管理" name="members">
        <div class="member-toolbar">
          <div>
            <strong>{{ currentSpaceName }}</strong>
            <span>{{ members.length }} 位成员</span>
          </div>
          <el-button icon="el-icon-refresh" size="small" @click="loadMembers">刷新</el-button>
        </div>

        <form v-if="canInviteMembers" class="member-invite-form" @submit.prevent="handleInviteMember">
          <el-input
            v-model.trim="inviteForm.identifier"
            autocomplete="off"
            placeholder="手机号、邮箱或用户名"
          />
          <el-select v-model="inviteForm.role" aria-label="成员角色">
            <el-option
              v-for="option in roleOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <el-button :loading="inviting" native-type="submit" type="primary">{{ directMemberLabel }}</el-button>
        </form>

        <div v-loading="loadingMembers" class="member-list" aria-live="polite">
          <el-empty v-if="!loadingMembers && !members.length" description="暂无成员" :image-size="72" />
          <div v-for="member in members" :key="member.membership_id" class="member-row">
            <span class="member-avatar" aria-hidden="true">{{ memberInitial(member) }}</span>
            <div class="member-row__identity">
              <strong>{{ memberName(member) }}</strong>
              <span v-if="memberContact(member)">{{ memberContact(member) }}</span>
            </div>
            <div class="member-row__roles">
              <template v-if="isOwner(member)">
                <el-tag size="mini" type="warning">空间所有者</el-tag>
              </template>
              <el-select
                v-else-if="canUpdateMember(member)"
                v-model="memberRoleDrafts[member.membership_id]"
                multiple
                size="mini"
                @change="handleUpdateRoles(member)"
              >
                <el-option
                  v-for="option in roleOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
              <template v-else>
                <el-tag v-for="role in member.role_codes" :key="role" size="mini" type="info">
                  {{ roleLabel(role) }}
                </el-tag>
              </template>
            </div>
            <el-tag :type="member.status === 'active' ? 'success' : 'info'" size="mini">
              {{ memberStatusLabel(member.status) }}
            </el-tag>
            <div class="member-row__actions">
              <el-button
                v-if="canTransferOwnership(member)"
                :loading="memberActionKey === `${member.membership_id}:transfer`"
                size="mini"
                type="warning"
                plain
                @click="handleTransferOwnership(member)"
              >转移所有权</el-button>
              <el-button
                v-if="canUpdateMember(member)"
                :loading="memberActionKey === `${member.membership_id}:status`"
                size="mini"
                @click="handleToggleStatus(member)"
              >{{ member.status === "active" ? "停用" : "启用" }}</el-button>
              <el-button
                v-if="canRemoveMember(member)"
                :loading="memberActionKey === `${member.membership_id}:remove`"
                size="mini"
                type="danger"
                plain
                @click="handleRemoveMember(member)"
              >移除</el-button>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <slot name="extra-tabs" :space-id="currentSpaceID" :space-name="currentSpaceName" :context="currentContext" :active-tab="activeTab" />

      <el-tab-pane
        v-if="canManageApplications"
        label="已安装应用"
        lazy
        name="applications"
      >
        <SpaceApplicationsPanel
          :space-id="currentSpaceID"
          :space-name="currentSpaceName"
        />
      </el-tab-pane>
    </el-tabs>
  </el-dialog>
</template>

<script>
import { authState, restoreSession } from "@/services/auth";
import SpaceApplicationsPanel from "./SpaceApplicationsPanel.vue";
import { spaceDisplayLabel, userDisplayLabel, memberContactLabel } from "@/services/identityLabels";
import {
  SPACE_ROLE_OPTIONS,
  createSpace,
  getCurrentSpaceContext,
  inviteSpaceMember,
  leaveSpace,
  listMySpaces,
  listSpaceMembers,
  removeSpaceMember,
  setDefaultSpace,
  switchCurrentSpace,
  transferSpaceOwnership,
  updateSpaceMemberRoles,
  updateSpaceMemberStatus,
} from "@/services/spaces";

export default {
  name: "SpaceManagementDialog",
  components: {
    SpaceApplicationsPanel,
  },
  props: {
    directMemberLabel: { type: String, default: "邀请成员" },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "spaces",
      spaces: [],
      currentContext: { space: null, roles: [], permissions: [] },
      members: [],
      memberRoleDrafts: {},
      loadingSpaces: false,
      loadingMembers: false,
      creating: false,
      inviting: false,
      switchingSpaceID: "",
      defaultingSpaceID: "",
      memberActionKey: "",
      showCreateForm: false,
      createForm: { name: "", slug: "" },
      inviteForm: { identifier: "", role: "editor" },
      roleOptions: SPACE_ROLE_OPTIONS,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    currentSpaceID() {
      const contextID = this.currentContext.space && this.currentContext.space.id;
      const sessionID = authState.session && authState.session.current_space
        ? authState.session.current_space.id
        : "";
      return String(contextID || sessionID || "");
    },
    currentSpaceName() {
      return this.spaceLabel(this.currentContext.space || (authState.session && authState.session.current_space));
    },
    currentUserID() {
      return String(authState.session && authState.session.user
        ? authState.session.user.id || ""
        : "");
    },
    currentPermissions() {
      return Array.isArray(this.currentContext.permissions) ? this.currentContext.permissions : [];
    },
    isCurrentOwner() {
      return Array.isArray(this.currentContext.roles)
        && this.currentContext.roles.includes("space_owner");
    },
    canManageApplications() {
      const roles = Array.isArray(this.currentContext.roles) ? this.currentContext.roles : [];
      return roles.includes("space_owner") || roles.includes("space_admin");
    },
    canInviteMembers() {
      return this.currentPermissions.includes("member:invite");
    },
  },
  methods: {
    async handleOpen() {
      await this.loadSpaceOverview();
    },
    handleClosed() {
      this.activeTab = "spaces";
      this.showCreateForm = false;
      this.resetCreateForm();
      this.inviteForm = { identifier: "", role: "editor" };
    },
    async handleTabClick(tab) {
      if (tab && tab.name === "members") {
        await this.loadMembers();
      }
    },
    async loadSpaceOverview() {
      this.loadingSpaces = true;
      try {
        const [spaces, context] = await Promise.all([
          listMySpaces(),
          getCurrentSpaceContext(),
        ]);
        this.spaces = spaces;
        this.currentContext = context;
      } catch (error) {
        this.spaces = [];
        this.$message.error(error.message || "加载空间失败");
      } finally {
        this.loadingSpaces = false;
      }
    },
    async loadMembers() {
      if (!this.currentSpaceID || this.loadingMembers) {
        return;
      }
      this.loadingMembers = true;
      try {
        const result = await listSpaceMembers(this.currentSpaceID);
        this.members = result.members;
        this.memberRoleDrafts = {};
        this.members.forEach((member) => {
          this.$set(
            this.memberRoleDrafts,
            member.membership_id,
            Array.isArray(member.role_codes) ? member.role_codes.filter((role) => role !== "space_owner") : [],
          );
        });
      } catch (error) {
        this.members = [];
        this.$message.error(error.message || "加载空间成员失败");
      } finally {
        this.loadingMembers = false;
      }
    },
    resetCreateForm() {
      this.createForm = { name: "", slug: "" };
      this.showCreateForm = false;
    },
    async handleCreateSpace() {
      if (this.creating) return;
      if (!String(this.createForm.name || "").trim()) {
        this.$message.warning("请输入空间名称");
        return;
      }
      this.creating = true;
      try {
        await createSpace(this.createForm);
        await this.restoreAndReload("空间已创建");
      } catch (error) {
        this.$message.error(error.message || "创建空间失败");
      } finally {
        this.creating = false;
      }
    },
    async handleSwitchSpace(space) {
      if (!space || this.isCurrentSpace(space) || this.switchingSpaceID) return;
      this.switchingSpaceID = space.id;
      try {
        await switchCurrentSpace(space.id);
        await this.restoreAndReload(`已切换到${this.spaceLabel(space)}`);
      } catch (error) {
        this.$message.error(error.message || "切换空间失败");
      } finally {
        this.switchingSpaceID = "";
      }
    },
    async handleSetDefault(space) {
      if (!space || space.is_default || this.defaultingSpaceID) return;
      this.defaultingSpaceID = space.id;
      try {
        await setDefaultSpace(space.id);
        await this.restoreAndReload("默认空间已更新");
      } catch (error) {
        this.$message.error(error.message || "设置默认空间失败");
      } finally {
        this.defaultingSpaceID = "";
      }
    },
    async handleLeaveSpace(space) {
      if (!space) return;
      if (this.isCurrentSpace(space) && this.isCurrentOwner) {
        this.$message.warning("空间所有者需先转移所有权后才能退出");
        return;
      }
      try {
        await this.$confirm(
          `确认退出“${this.spaceLabel(space)}”？`,
          "退出空间",
          { type: "warning", confirmButtonText: "确认退出", cancelButtonText: "取消" },
        );
      } catch (error) {
        return;
      }
      try {
        await leaveSpace(space.id);
        await this.restoreAndReload("已退出空间");
      } catch (error) {
        if (Number(error && error.status || 0) === 409 || (error && error.code === "conflict")) {
          this.$message.warning("空间所有者需先转移所有权后才能退出");
          return;
        }
        this.$message.error(error.message || "退出空间失败");
      }
    },
    async restoreAndReload(message) {
      const session = await restoreSession();
      if (!session) {
        throw new Error("会话刷新失败，请重新登录");
      }
      this.$message.success(message);
      if (typeof window !== "undefined" && window.location && typeof window.location.reload === "function") {
        window.location.reload();
      }
    },
    async handleInviteMember() {
      if (this.inviting) return;
      if (!String(this.inviteForm.identifier || "").trim()) {
        this.$message.warning("请输入成员账号");
        return;
      }
      this.inviting = true;
      try {
        await inviteSpaceMember(this.currentSpaceID, {
          identifier: this.inviteForm.identifier,
          roleCodes: [this.inviteForm.role],
        });
        this.inviteForm.identifier = "";
        await this.loadMembers();
        this.$message.success("成员已加入空间");
      } catch (error) {
        this.$message.error(error.message || "邀请成员失败");
      } finally {
        this.inviting = false;
      }
    },
    async handleUpdateRoles(member) {
      if (!this.canUpdateMember(member)) return;
      const roles = this.memberRoleDrafts[member.membership_id] || [];
      if (!roles.length) {
        this.$message.warning("至少保留一个成员角色");
        this.$set(this.memberRoleDrafts, member.membership_id, member.role_codes.slice());
        return;
      }
      const key = `${member.membership_id}:roles`;
      this.memberActionKey = key;
      try {
        const updated = await updateSpaceMemberRoles(this.currentSpaceID, member.membership_id, roles);
        this.replaceMember(updated);
        this.$message.success("成员角色已更新");
      } catch (error) {
        this.$set(this.memberRoleDrafts, member.membership_id, member.role_codes.slice());
        this.$message.error(error.message || "更新成员角色失败");
      } finally {
        if (this.memberActionKey === key) this.memberActionKey = "";
      }
    },
    async handleToggleStatus(member) {
      if (!this.canUpdateMember(member)) return;
      const key = `${member.membership_id}:status`;
      this.memberActionKey = key;
      try {
        const status = member.status === "active" ? "suspended" : "active";
        const updated = await updateSpaceMemberStatus(this.currentSpaceID, member.membership_id, status);
        this.replaceMember(updated);
        this.$message.success(status === "active" ? "成员已启用" : "成员已停用");
      } catch (error) {
        this.$message.error(error.message || "更新成员状态失败");
      } finally {
        if (this.memberActionKey === key) this.memberActionKey = "";
      }
    },
    async handleTransferOwnership(member) {
      if (!this.canTransferOwnership(member)) return;
      try {
        await this.$confirm(
          `确认将“${this.memberName(member)}”设为空间所有者？`,
          "转移所有权",
          { type: "warning", confirmButtonText: "确认转移", cancelButtonText: "取消" },
        );
      } catch (error) {
        return;
      }
      const key = `${member.membership_id}:transfer`;
      this.memberActionKey = key;
      try {
        await transferSpaceOwnership(this.currentSpaceID, member.membership_id);
        await this.restoreAndReload("空间所有权已转移");
      } catch (error) {
        this.$message.error(error.message || "转移所有权失败");
      } finally {
        if (this.memberActionKey === key) this.memberActionKey = "";
      }
    },
    async handleRemoveMember(member) {
      if (!this.canRemoveMember(member)) return;
      try {
        await this.$confirm(
          `确认将“${this.memberName(member)}”移出当前空间？`,
          "移除成员",
          { type: "warning", confirmButtonText: "确认移除", cancelButtonText: "取消" },
        );
      } catch (error) {
        return;
      }
      const key = `${member.membership_id}:remove`;
      this.memberActionKey = key;
      try {
        await removeSpaceMember(this.currentSpaceID, member.membership_id);
        this.members = this.members.filter((item) => item.membership_id !== member.membership_id);
        this.$message.success("成员已移除");
      } catch (error) {
        this.$message.error(error.message || "移除成员失败");
      } finally {
        if (this.memberActionKey === key) this.memberActionKey = "";
      }
    },
    replaceMember(updated) {
      if (!updated) return;
      const index = this.members.findIndex((member) => member.membership_id === updated.membership_id);
      if (index >= 0) this.$set(this.members, index, updated);
      this.$set(
        this.memberRoleDrafts,
        updated.membership_id,
        Array.isArray(updated.role_codes) ? updated.role_codes.filter((role) => role !== "space_owner") : [],
      );
    },
    isCurrentSpace(space) {
      return String(space && space.id || "") === this.currentSpaceID;
    },
    isOwner(member) {
      return Array.isArray(member && member.role_codes) && member.role_codes.includes("space_owner");
    },
    isSelf(member) {
      return String(member && member.user && member.user.id || "") === this.currentUserID;
    },
    canUpdateMember(member) {
      return this.currentPermissions.includes("member:update") && !this.isSelf(member) && !this.isOwner(member);
    },
    canRemoveMember(member) {
      return this.currentPermissions.includes("member:remove") && !this.isSelf(member) && !this.isOwner(member);
    },
    canTransferOwnership(member) {
      return this.isCurrentOwner
        && member
        && member.status === "active"
        && !this.isSelf(member)
        && !this.isOwner(member);
    },
    roleLabel(role) {
      if (role === "space_owner") return "空间所有者";
      const option = this.roleOptions.find((item) => item.value === role);
      return option ? option.label : role;
    },
    memberStatusLabel(status) {
      if (status === "active") return "正常";
      if (status === "suspended") return "已停用";
      if (status === "invited") return "待加入";
      return status || "未知";
    },
    memberName(member) {
      return userDisplayLabel(member && member.user);
    },
    memberContact(member) {
      return memberContactLabel(member && member.user);
    },
    memberInitial(member) {
      return this.memberName(member).slice(0, 1).toUpperCase();
    },
    spaceInitial(space) {
      return this.spaceLabel(space).slice(0, 1).toUpperCase();
    },
    spaceLabel(space) {
      return spaceDisplayLabel(space, authState.session && authState.session.user);
    },
  },
};
</script>

<style lang="scss" scoped>
.space-management-dialog {
  ::v-deep .space-management-dialog__panel {
    max-width: calc(100vw - 32px);
    border-radius: 6px;
  }

  ::v-deep .el-dialog__header {
    padding: 20px 24px 12px;
    border-bottom: 1px solid #eef0f3;
  }

  ::v-deep .el-dialog__body {
    max-height: calc(90vh - 76px);
    padding: 0 24px 24px;
    overflow-y: auto;
  }

  ::v-deep .el-tabs__item.is-active,
  ::v-deep .el-tabs__item:hover {
    color: #f78001;
  }

  ::v-deep .el-tabs__active-bar {
    background-color: #f78001;
  }

  ::v-deep .el-button--primary {
    border-color: #f78001;
    background: #f78001;
  }
}

.space-toolbar,
.member-toolbar,
.space-row,
.member-row {
  display: flex;
  align-items: center;
}

.space-toolbar,
.member-toolbar {
  min-height: 52px;
  justify-content: space-between;
  gap: 16px;
}

.space-toolbar__identity,
.member-toolbar > div {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 10px;

  span {
    color: #8a929d;
    font-size: 13px;
  }

  strong {
    overflow: hidden;
    color: #2a303a;
    font-size: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.space-create-form,
.member-invite-form {
  display: grid;
  gap: 12px;
  margin: 8px 0 16px;
  padding: 16px;
  border: 1px solid #e4e8ee;
  border-radius: 6px;
  background: #f8f9fb;
}

.space-create-form {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
}

.space-create-form__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.member-invite-form {
  grid-template-columns: minmax(220px, 1fr) 180px auto;
}

.space-list,
.member-list {
  min-height: 160px;
}

.space-row,
.member-row {
  min-height: 72px;
  gap: 14px;
  padding: 10px 12px;
  border-bottom: 1px solid #edf0f3;
}

.space-row.is-current {
  background: #fff8f0;
}

.space-avatar,
.member-avatar {
  display: inline-flex;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #313845;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.member-avatar {
  border-radius: 50%;
  background: #f78001;
}

.space-row__main,
.member-row__identity {
  min-width: 0;
  flex: 1 1 auto;

  > span {
    display: block;
    margin-top: 5px;
    overflow: hidden;
    color: #8a929d;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.space-row__title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;

  strong {
    overflow: hidden;
    color: #2a303a;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.space-row__actions,
.member-row__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.space-row__leave {
  color: #d45b5b;
}

.member-row__identity {
  flex: 0 1 190px;
}

.member-row__roles {
  display: flex;
  min-width: 180px;
  flex: 1 1 260px;
  flex-wrap: wrap;
  gap: 5px;

  ::v-deep .el-select {
    width: 100%;
    max-width: 280px;
  }
}

@media (max-width: 767px) {
  .space-management-dialog {
    ::v-deep .space-management-dialog__panel {
      width: calc(100vw - 24px) !important;
      max-width: none;
      margin: 12px auto 0;
    }

    ::v-deep .el-dialog__header {
      padding: 16px 16px 10px;
    }

    ::v-deep .el-dialog__body {
      max-height: calc(100vh - 72px);
      padding: 0 16px 16px;
    }
  }

  .space-create-form,
  .member-invite-form {
    grid-template-columns: minmax(0, 1fr);
  }

  .space-create-form__actions {
    justify-content: flex-end;
  }

  .space-row,
  .member-row {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .space-row__main {
    min-width: calc(100% - 54px);
  }

  .space-row__actions,
  .member-row__actions {
    width: 100%;
    justify-content: flex-end;
  }

  .member-row__identity {
    min-width: calc(100% - 54px);
    flex-basis: calc(100% - 54px);
  }

  .member-row__roles {
    min-width: 0;
    flex-basis: 100%;
  }
}
</style>
